import { DEFAULT_MEASUREMENT } from "@utils/default";
import { useSession } from "next-auth/react";
import { useFormatter, useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import Link from "next/link";
import { useContext, useEffect, useMemo, useState } from "react";

import AddToFavorites from "@/components/AddToFavorites/AddToFavorites";
import CardTags from "@/components/AutogeneratedCarousel/CardTags";
import InnerCardCarousel from "@/components/AutogeneratedCarousel/InnerCardCarousel";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { UserContext } from "@/context/UserContext";
import { ecomProductCardClick, propertyCardClickTracking } from "@/dataLayer/dataLayer";
import { useProjectUrl } from "@/hooks/useProjectUrl";
import { UnitTypeCardListProps } from "@/types";
import cn from "@/utils/cn";
import { getQuarter } from "@/utils/dateUtils";

export default function UnitTypesCardList({
  item,
  customClass,
  serverSideCookieCurrency,
  currencyFromApiRes,
  isSearchResultPage = false,
}: UnitTypeCardListProps) {
  const t = useTranslations();
  const { currency, measurement } = useContext(UserContext);
  const format = useFormatter();
  const locale = useLocale();
  const { data: session } = useSession();
  const [isLoadingForPrice, setIsLoadingForPrice] = useState(true);
  const formattedPrice = format
    .number(Math.round(Number(item.price) / 100) || 0, {
      style: "currency",
      currency: currency,
      trailingZeroDisplay: "stripIfInteger",
      currencyDisplay: locale?.split("-")?.[1] === "ae" ? "code" : "symbol",
    })
    ?.replace(/(\p{Sc})(\d)/u, "$1 $2")
    ?.replaceAll("UK", "");
  const deliveryDate = getQuarter(item.availabilityDate!);
  const cardTitle = useMemo(() => {
    //TODO uniform this as function
    if (item.bedrooms.value) {
      if (item.bedrooms.value.toString().toLowerCase() === "studio") {
        return t("label.propertyType.studio") + " " + t(`label.propertyType.type.${item.unitType ?? ""}`);
      } else {
        return t("label.propertyType.brName", { bedrooms: `${item.bedrooms.value}` }) + " " + t(`label.propertyType.type.${item.unitType ?? ""}`);
      }
    } else {
      return t("label.propertyType.studio");
    }
  }, [item]);

  useEffect(() => {
    if (serverSideCookieCurrency && currencyFromApiRes && currency) {
      if (serverSideCookieCurrency === currencyFromApiRes && currencyFromApiRes === currency) {
        setIsLoadingForPrice(false);
      } else {
        setIsLoadingForPrice(true);
      }
    }
  }, [serverSideCookieCurrency, currencyFromApiRes, currency]);

  // Communities matching and creating a URL - Start
  const finalUrl = useProjectUrl({ item, locale, isPropertyType: true });
  // console.log("finalUrlfinalUrl", item);
  // Communities matching and creating a URL - End
  return (
    <article
      className={cn(
        `group flex flex-col rounded-lg stroke-black p-0 text-black transition-shadow duration-500 md:p-xs md:hover:bg-[#fff9] md:hover:shadow-extra-strong`,
        customClass,
      )}
    >
      <div className="relative">
        {item.images.length > 1 ? (
          <InnerCardCarousel item={item} />
        ) : (
          <Image
            className="aspect-4-3 shadow-strong rounded-lg"
            alt={item?.images?.[0]?.title || ""}
            src={item?.images?.[0]?.url}
            width={260}
            height={195}
          />
        )}

        {item.tags && item.tags.length > 0 && <CardTags tags={item.tags} className="z-20 w-[60%] md:absolute md:w-[80%]" />}

        <AddToFavorites item={item} className="absolute right-xs top-xs z-10 hidden md:block" setTransparent={true} />

        <div className="mt-m flex flex-col gap-s text-s font-secondary-light">
          <h3 className="flex items-center text-m font-medium">
            <span>
              {cardTitle}
              {item?.status && <span className="font-secondary-light before:ml-xxs before:mr-xs before:content-['\\2022']">{item.status}</span>}
            </span>
            <AddToFavorites item={item} className="relative z-20 ml-auto block md:hidden" fillColor="fill-black" />
          </h3>
          <p>{t(item.location)}</p>
          <p className="font-normal">
            {t("label.common.from") + " "}
            {!isSearchResultPage && isLoadingForPrice ? t("common.loading") : formattedPrice}
          </p>
          <div className="grid grid-cols-2 gap-xs font-secondary-light">
            <div className="flex items-center gap-xxs">
              <Icon name="bedroom" size="16" title="bed" />
              {(item.bedrooms.value as string).trim().toLowerCase() !== "studio"
                ? t(`${item?.bedrooms?.value} ${item?.bedrooms?.label}`)
                : t(item.bedrooms?.label)}
            </div>
            <p className="flex items-center gap-xxs">
              <Icon name="size" size="16" title="turnkey" />
              {t(`label.project.upTo`)}
              {Math.round(Number(item.sqFt?.value))} {t(`label.project.${measurement === DEFAULT_MEASUREMENT ? "sq.ft" : "sq.mt"}`)}
            </p>
            {/*<p className="flex items-center gap-xxs">
               <Icon name="furnished" size="16" title="furniture" />
              {item.furniture} 
            </p>*/}
            <p data-availability={item.availabilityDate} className="flex items-center gap-xxs">
              <Icon name="turnkey" size="16" title="available" />
              {deliveryDate !== "ready" ? deliveryDate : t("label.ready")}
            </p>
          </div>
          <div className="invisible hidden items-center font-medium md:block md:group-hover:visible">
            <Button
              href={item.cta.link.url}
              // href={finalUrl}
              size={ButtonSize.SMALL}
              theme={ButtonTheme.BLACK}
              type="button"
              variant={ButtonVariant.PRIMARY}
              customClass="flex justify-center w-full"
            >
              {t(item.cta.label === "explore" ? "label.explore" : item.cta.label)}
            </Button>
          </div>
        </div>
        <Link
          href={finalUrl}
          // href={`${item.cta.link.url}${filteredResults?.length === 1 ? `/${filteredResults[0]?.name?.replaceAll("/", "-")}/` : ""}`}
          className="absolute left-0 top-0 h-full w-full"
          data-title={cardTitle}
          onClick={() => {
            const projectTitle = item.title?.replaceAll(" ", "-").toLowerCase();
            const projectName = item.project?.replaceAll(" ", "-").toLowerCase();
            const productIdentifier = `${projectName ?? ""}/${projectTitle ?? ""}`;

            propertyCardClickTracking(item.project || "");
            ecomProductCardClick({
              product_identifier: productIdentifier,
              card_name: item.title,
              property_name: item.project || "",
              project_location: item.location || "",
              project_type: item.unitType || "",
              bedroom_numbers: (item.bedrooms.value ? (item.bedrooms.value as string) : "") || ("" as string),
              unit_type: item.furniture || "",
              unit_size: `${Math.round(Number(item.sqFt?.value))} ${t(`label.project.${measurement === DEFAULT_MEASUREMENT ? "sq.ft" : "sq.mt"}`)}`,
              unit_price: Math.round(Number(item.price) / 100),
              currency: currency,
              posession_time: getQuarter(item.availabilityDate!),
              user_logged_in: !!session,
              // user_id: session?.user.id || "",
            });
          }}
        >
          <span className="sr-only">{item.cta.label}</span>
        </Link>
      </div>
    </article>
  );
}
